/**
 * useLinkedRecords Composable
 *
 * Drop-in replacement for useRecords when the consumer needs links/backlinks
 * attached. Owns the full pipeline:
 *
 *   1. Fetch records via useRecords (`query` + `variables`)
 *   2. Fetch slim link rows via useLinks, keyed on the ids of (1) — every
 *      record_link touching at least one loaded record, with no nested
 *      endpoint records
 *   3. Derive the set of linked record ids (record_id, linked_id) not already
 *      present in (1), fetch them once via the internal linked-records-by-ids
 *      query
 *   4. Build a lookup over (1) + (3), attach resolved `record` and `linked`
 *      onto each link row, group as `links` (by record_id) and `backlinks`
 *      (by linked_id), and expose an enriched `items` view
 *
 * Driving the links query off the actual loaded ids (rather than a record
 * type) means useLinkedRecords works with any useRecords query — filtered
 * subsets, id lists, mixed-type results, dynamic lists, etc.
 *
 * Replaces the old fat enrichment query that repeated RequiredRecordFields
 * for both endpoints on every link row (the same popular target records were
 * shipped over and over).
 *
 * Returns the same shape as useRecords; the `items` ref is the enriched view.
 *
 * @example
 * const records = useLinkedRecords({
 *   query: fetchRecordsBrowser,
 *   variables,
 *   recordType,                   // optional; drives backlinkFields
 *   excludeBacklinkTypes: ["document"],  // optional; defaults to ["document"]
 * }, deps)
 */

import { computed, type MaybeRefOrGetter, ref, shallowRef, toValue, watch } from "vue"
import { buildBacklinkFields } from "@auger/core/fields"
import type {
  RecordLinkFlatFieldsFragment,
  RequiredRecordFieldsFragment,
} from "@auger/core/gql/types"
import { fetchRequiredRecordsByIds } from "@/common/hasura/records"
import type { ComposableDeps } from "@/composables/types"
import { useSchemasStore } from "@/stores/schemas"
import { useLinks } from "./useLinks"
import {
  type AnyRecordsDoc,
  type RecordItemOf,
  useRecords,
  type UseRecordsOptions,
} from "./useRecords"

/** A link endpoint: a loaded record, or one fetched by id for the lookup. */
type LinkEnd<TDoc> = RecordItemOf<TDoc> | RequiredRecordFieldsFragment

/** A slim link row with both of its endpoints resolved from the lookup. */
export type ResolvedLink<TDoc> = RecordLinkFlatFieldsFragment & {
  record: LinkEnd<TDoc> | null
  linked: LinkEnd<TDoc> | null
}

/**
 * A loaded record with its links attached. `links` and `backlinks` are absent
 * until the link rows have loaded and merged.
 */
export type LinkedRecordItem<TDoc> = RecordItemOf<TDoc> & {
  links?: ResolvedLink<TDoc>[]
  backlinks?: ResolvedLink<TDoc>[]
}

const DEFAULT_EXCLUDE_BACKLINK_TYPES = ["document"]

// Match useLinks: chunk large id sets so each Postgres `IN (...)` stays
// in the cheap-plan range. Bounded concurrency stops a 10k-record type from
// fanning out 20+ simultaneous queries and saturating the Hasura pool.
const ID_CHUNK_SIZE = 500
const CHUNK_CONCURRENCY = 5

function chunk<T>(array: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size))
  return out
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  async function worker() {
    while (true) {
      const i = next++
      if (i >= items.length) return
      results[i] = await fn(items[i]!, i)
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, worker)
  await Promise.all(workers)
  return results
}

export function useLinkedRecords<TDoc extends AnyRecordsDoc>(
  options: UseRecordsOptions<TDoc> & {
    /** Drives backlinkFields. */
    recordType?: MaybeRefOrGetter<string | null | undefined>
    excludeBacklinkTypes?: string[]
  },
  deps: ComposableDeps
) {
  const { client } = deps
  const {
    query,
    variables,
    recordType = null,
    excludeBacklinkTypes = DEFAULT_EXCLUDE_BACKLINK_TYPES,
  } = options

  if (!query || !variables) {
    throw new Error("useLinkedRecords: query & variables are required")
  }

  const records = useRecords<TDoc>({ query, variables }, deps)
  const ids = computed(() => records.items.value.map((r) => r.id))
  const linksFetch = useLinks({ ids, excludeBacklinkTypes }, deps)
  const schemasStore = useSchemasStore()
  const getRecordType = () => toValue(recordType)

  // Enriched view of records.items with `links` and `backlinks` attached.
  // Starts empty; replaced wholesale each merge cycle.
  const items = shallowRef<LinkedRecordItem<TDoc>[]>([])
  const linksMerging = ref(false)

  // Backlink fields — derived from schemas elsewhere that have link fields
  // pointing to this record type. Only surfaces fields where at least one
  // loaded record actually has incoming links of that kind.
  const backlinkFields = computed(() => {
    const type = getRecordType()
    if (!type) return []
    const all = buildBacklinkFields(type, schemasStore.schemas)
    const list = items.value
    if (!list.length) return all
    return all.filter((field) =>
      list.some((item) => {
        const v = field.value?.(item)
        return Array.isArray(v) && v.length > 0
      })
    )
  })

  // Records' own fields, plus backlinks contributed by the link graph.
  const fields = computed(() => [...records.fields.value, ...backlinkFields.value])

  function indexById<R extends { id: number }>(list: readonly R[]): Map<number, R> {
    const map = new Map<number, R>()
    for (const r of list || []) {
      if (r?.id != null) map.set(r.id, r)
    }
    return map
  }

  /**
   * Group slim link rows into per-record links/backlinks maps, decorating each
   * row with resolved `record` and `linked` from the lookup.
   */
  function buildLinkMaps(
    links: readonly RecordLinkFlatFieldsFragment[],
    lookup: Map<number, LinkEnd<TDoc>>,
    ownerIds: Set<number>
  ) {
    const linksByRecordId = new Map<number, ResolvedLink<TDoc>[]>()
    const backlinksByLinkedId = new Map<number, ResolvedLink<TDoc>[]>()
    for (const link of links) {
      const resolved: ResolvedLink<TDoc> = {
        ...link,
        record: link.record_id != null ? (lookup.get(link.record_id) ?? null) : null,
        linked: link.linked_id != null ? (lookup.get(link.linked_id) ?? null) : null,
      }
      if (link.record_id != null && ownerIds.has(link.record_id)) {
        const arr = linksByRecordId.get(link.record_id) || []
        arr.push(resolved)
        linksByRecordId.set(link.record_id, arr)
      }
      if (link.linked_id != null && ownerIds.has(link.linked_id)) {
        const arr = backlinksByLinkedId.get(link.linked_id) || []
        arr.push(resolved)
        backlinksByLinkedId.set(link.linked_id, arr)
      }
    }
    return { linksByRecordId, backlinksByLinkedId }
  }

  // Cache for the linked-records lookup keyed by the set of ids requested.
  // The records(_in) payload is the dominant cost of a refresh cycle, but
  // it's invariant under local record mutations (an optimistic updateItem
  // doesn't change the set of linked ids), so we reuse the lookup across
  // re-merges with the same id set.
  let linkedCache: { signature: string | null; lookup: Map<number, LinkEnd<TDoc>> } = {
    signature: null,
    lookup: new Map(),
  }

  /**
   * Recompute the enriched items view. Triggers on records or links changes.
   * Runs the linked-records fetch only when the link set changes; record-only
   * changes reuse the cached lookup.
   */
  // Records whose links haven't merged: `links` and `backlinks` stay absent.
  const unlinked = (list: RecordItemOf<TDoc>[]): LinkedRecordItem<TDoc>[] =>
    list.map((r) => ({ ...r, links: undefined, backlinks: undefined }))

  let mergeToken = 0
  async function merge() {
    const recordItems = records.items.value
    const links = linksFetch.links.value

    if (!recordItems.length) {
      items.value = []
      return
    }
    if (!links) {
      items.value = unlinked(recordItems)
      return
    }

    const start = ++mergeToken
    linksMerging.value = true
    try {
      const recordLookup = indexById(recordItems)
      const recordIds = new Set(recordLookup.keys())

      // Linked ids needed = union of link endpoints, minus ids we already have.
      const needed = new Set<number>()
      for (const link of links) {
        if (link.record_id != null && !recordIds.has(link.record_id)) {
          needed.add(link.record_id)
        }
        if (link.linked_id != null && !recordIds.has(link.linked_id)) {
          needed.add(link.linked_id)
        }
      }

      // Reuse cached lookup if the needed set is unchanged.
      const signature = [...needed].sort((a, b) => a - b).join(",")
      let linkedLookup: Map<number, LinkEnd<TDoc>>
      if (signature === linkedCache.signature) {
        linkedLookup = linkedCache.lookup
      } else if (needed.size === 0) {
        linkedLookup = new Map()
        linkedCache = { signature, lookup: linkedLookup }
      } else {
        // Chunk with bounded concurrency so each `IN (...)` stays small without
        // fanning out dozens of simultaneous queries. Input ids are already a
        // deduped Set, so chunks are disjoint — no cross-chunk row collisions.
        const chunks = chunk([...needed], ID_CHUNK_SIZE)
        const results = await mapWithConcurrency(chunks, CHUNK_CONCURRENCY, (chunkIds) =>
          client
            .query({
              query: fetchRequiredRecordsByIds,
              variables: { ids: chunkIds },
              fetchPolicy: "no-cache",
            })
            .then(({ data }) => [...(data?.records ?? [])])
        )
        if (start !== mergeToken) return
        const rows = results.flat()
        linkedLookup = indexById(rows)
        linkedCache = { signature, lookup: linkedLookup }
      }

      // Records win when an id is in both pools (the full record carries data,
      // schema, etc.; the linked-records fetch only carries RequiredRecordFields).
      const lookup = new Map<number, LinkEnd<TDoc>>(linkedLookup)
      for (const [id, rec] of recordLookup) lookup.set(id, rec)

      const { linksByRecordId, backlinksByLinkedId } = buildLinkMaps(links, lookup, recordIds)

      items.value = recordItems.map((r) => ({
        ...r,
        links: linksByRecordId.get(r.id) ?? [],
        backlinks: backlinksByLinkedId.get(r.id) ?? [],
      }))
    } catch (err) {
      // useRecords carries the user-facing failure path; a linked-records
      // failure just leaves items un-enriched for this cycle.
      console.error("[useLinkedRecords] linked records fetch failed:", err)
      items.value = unlinked(recordItems)
    } finally {
      if (start === mergeToken) linksMerging.value = false
    }
  }

  watch([records.items, linksFetch.links], merge, { immediate: true })

  return {
    // useRecords shape — items + fields shadowed with the enriched view
    ...records,
    items,
    fields,
    backlinkFields,

    // Link-pipeline extras
    linksLoading: linksFetch.loading,
    linksError: linksFetch.error,
    linksMerging,
    refreshLinks: linksFetch.refresh,
  }
}
